import type { ProductCustomCardItem } from '@/types/ProductType'
import { formatPriceWithCurrency } from '@/utils/currencyFormat'
import demoProductImage from './assets/products/demo-product.webp'

export const FAIR_HOME_PRODUCTS_DEFAULT_AUDIENCE = 'woman'

/** Figma `1:8797` — 5×4 grid */
export const FAIR_HOME_PRODUCTS_PAGE_SIZE = 20

export const FAIR_HOME_PRODUCTS_AI_TIP_KEY = 'products_liked_past_week' as const

export type FairHomeProductsDensity = 'comfortable' | 'compact'

export interface FairHomeProductCard {
  id: number
  variantId: number
  brand: string
  title: string
  price: string
  oldPrice?: string
  images: string[]
  isFavorite: boolean
  isDemo?: boolean
}

interface ProductCardFields {
  imageUrls?: string[]
  image_urls?: string[]
  isFavorite?: boolean
  is_favorite?: boolean
  variantId?: number
  variant_id?: number
}

function getProductImages(item: ProductCustomCardItem & ProductCardFields): string[] {
  const urls = item.image_urls ?? item.imageUrls ?? []
  return urls.filter(Boolean)
}

function mapApiProduct(
  item: ProductCustomCardItem & ProductCardFields,
): FairHomeProductCard {
  const currency = item.currency ?? ''
  const oldAmount = item.discount?.price_without_discount
  return {
    id: item.id,
    variantId: item.variant_id ?? item.variantId ?? item.id,
    brand: item.brand,
    title: item.name,
    price: formatPriceWithCurrency(item.price.lte, currency),
    oldPrice:
      oldAmount != null
        ? formatPriceWithCurrency(oldAmount, currency)
        : undefined,
    images: getProductImages(item),
    isFavorite: item.is_favorite ?? item.isFavorite ?? false,
  }
}

/** Figma `1:8797` demo — used when API returns empty */
const FAIR_HOME_PRODUCTS_DEMO_BASE: FairHomeProductCard[] = Array.from(
  { length: FAIR_HOME_PRODUCTS_PAGE_SIZE },
  (_, index) => ({
    id: -(index + 1),
    variantId: -(index + 1),
    brand: 'Wildberries',
    title: 'Long silk evening dress with ties',
    price: '39$',
    oldPrice: index === 1 ? '1456$' : undefined,
    images: [demoProductImage],
    isFavorite: false,
    isDemo: true,
  }),
)

export function resolveFairHomeProducts(
  items: ProductCustomCardItem[] | undefined | null,
  options?: { allowDemoFallback?: boolean },
): FairHomeProductCard[] {
  const mapped = (items ?? []).map(mapApiProduct)
  if (mapped.length > 0) {
    return mapped
  }
  if (options?.allowDemoFallback) {
    return FAIR_HOME_PRODUCTS_DEMO_BASE.map((item) => ({ ...item }))
  }
  return []
}

export function getFairHomeProductsColumns(
  viewportWidth: number,
  density: FairHomeProductsDensity,
): number {
  if (viewportWidth >= 1024) {
    return density === 'compact' ? 6 : 5
  }
  if (viewportWidth >= 768) {
    return density === 'compact' ? 4 : 3
  }
  return density === 'compact' ? 3 : 2
}

export function getFairHomeProductsGap(viewportWidth: number): number {
  if (viewportWidth >= 1366) {
    return 40
  }
  return 20
}

export function getFairHomeProductsCardDevice(
  viewportWidth: number,
): 'desktop' | 'mobile' {
  return viewportWidth >= 1024 ? 'desktop' : 'mobile'
}

export function showFairHomeProductsDensityToggle(
  viewportWidth: number,
): boolean {
  return viewportWidth >= 1024
}

export function canFairHomeProductsLoadMore(
  productsLength: number,
  limit: number,
  isDemo: boolean,
): boolean {
  if (isDemo) {
    return false
  }
  return productsLength >= limit
}
